/**
 * @bhorosha/core — the purchase calculation, shared by the web and mobile apps.
 *
 * A line-for-line TypeScript port of the formulas in Medicine Purchase
 * Calculator v3.10. `test/parity.test.ts` runs these side by side with the
 * original code and fails if any number differs.
 *
 * All dates are UTC midnight `Date` objects; stored dates are "YYYY-MM-DD".
 */

export type ScheduleType = 'daily' | 'dialysis' | 'interval' | 'fixed' | 'course';

export interface Medicine {
  id: string;
  name: string;
  type: string;
  strength?: string;
  strength_unit?: string;
  schedule_type: ScheduleType;
  morning_before?: number; morning_after?: number;
  lunch_before?: number; lunch_after?: number;
  dinner_before?: number; dinner_after?: number;
  dialysis_per_session?: number;
  dialysis_per_week?: number;
  skip_on_dialysis_day?: boolean;
  interval_dose?: number;
  interval_days?: number;
  fixed_qty_per_month?: number;
  fixed_every_days?: number;
  /** Course (e.g. an antibiotic): first dose at start_date + course_start_time, then
   *  course_per_day doses a day, evenly spaced (4 a day = every 6 hours), course_total doses in all. */
  course_start_time?: string;
  course_per_day?: number;
  course_total?: number;
  course_dose?: number;
  on_hand?: number;
  qty_overrides?: Record<string, number>;
  when?: string;
  start_date?: string;
  duration_days?: number;
  end_date?: string;
  [extra: string]: unknown;
}

export interface Person {
  id: string;
  name: string;
  age?: string;
  gender?: string;
  /** 0 = Sunday … 6 = Saturday */
  dialysis_days?: number[];
  dialysis_time?: 'morning' | 'noon' | 'evening' | string;
  [extra: string]: unknown;
}

export interface MedicineType {
  id: string;
  name: string;
  icon: string;
  unit: string;
  round_enabled?: boolean;
  round_to?: number;
  [extra: string]: unknown;
}

export type NeedStatus = 'normal' | 'partial' | 'ended' | 'not_started';
export interface NeedResult { schedule: string; needed: number; status: NeedStatus; dateSuffix?: string }

// ---------- Numbers ----------
const FRACTION_DISPLAY: Record<string, string> = { 0.25: '¼', 0.5: '½', 0.75: '¾' };
const FRACTION_INPUT: Record<string, number> = {
  '¼': 0.25, '½': 0.5, '¾': 0.75, '1/4': 0.25, '1/2': 0.5, '3/4': 0.75,
};

/** 1.5 -> "1½", 0.25 -> "¼", 2 -> "2" */
export function fmtNum(n: unknown): string {
  const f = parseFloat(n as string);
  if (Number.isNaN(f)) return '0';
  if (Number.isInteger(f)) return String(f);
  const whole = Math.trunc(f);
  const frac = Math.round((f - whole) * 100) / 100;
  const glyph = FRACTION_DISPLAY[Math.abs(frac)];
  if (glyph === undefined) return f.toString();
  if (whole === 0) return glyph;
  return `${whole}${glyph}`;
}

/** Accepts "1", "0.5", "½", "1½", "1/2", "1 1/2". Throws on invalid input. */
export function parseNumberInput(input: unknown): number {
  if (input === null || input === undefined) return 0;
  const s = String(input).trim();
  if (!s) return 0;
  const compact = s.replace(/\s+/g, '');
  for (const [glyph, val] of Object.entries(FRACTION_INPUT)) {
    if (compact.endsWith(glyph)) {
      const wholePart = compact.slice(0, -glyph.length);
      if (!wholePart) return val;
      const w = parseFloat(wholePart);
      if (Number.isNaN(w)) throw new Error(`invalid number: ${s}`);
      return w + val;
    }
  }
  if (s.includes(' ') && s.includes('/')) {
    const parts = s.split(/\s+/);
    if (parts.length === 2 && parts[1].includes('/')) {
      const whole = parseFloat(parts[0]);
      const [num, den] = parts[1].split('/').map(Number);
      if (!Number.isNaN(whole) && den) return whole + num / den;
    }
  }
  if (compact.includes('/')) {
    const [num, den] = compact.split('/').map(Number);
    if (!Number.isNaN(num) && den) return num / den;
  }
  const v = parseFloat(compact);
  if (Number.isNaN(v)) throw new Error(`invalid number: ${s}`);
  return v;
}

/** Round up to the pharmacy pack step (default 5). */
export function roundPharmacy(n: number, step?: unknown): number {
  const st = parseInt(step as string, 10) > 0 ? parseInt(step as string, 10) : 5;
  const c = Math.ceil(n);
  if (c <= 0) return 0;
  return Math.ceil(c / st) * st;
}

export function exactQuantity(n: number): number {
  if (n <= 0) return 0;
  return Math.ceil(n);
}

// ---------- Dates ----------
export function parseDateISO(s: unknown): Date | null {
  if (!s) return null;
  const m = String(s).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Date.UTC(+y, +mo - 1, +d));
}
export function formatDateISO(dt: Date | null): string {
  if (!dt) return '';
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}
export function parseDateDMY(s: unknown): Date | null {
  if (!s) return null;
  const m = String(s).trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const [, d, mo, y] = m;
  const dt = new Date(Date.UTC(+y, +mo - 1, +d));
  if (dt.getUTCFullYear() !== +y || dt.getUTCMonth() !== +mo - 1 || dt.getUTCDate() !== +d) return null;
  return dt;
}
export function formatDateDMY(dt: Date | null): string {
  if (!dt) return '';
  return `${String(dt.getUTCDate()).padStart(2, '0')}/${String(dt.getUTCMonth() + 1).padStart(2, '0')}/${dt.getUTCFullYear()}`;
}
export function addDays(dt: Date, days: number): Date {
  const d = new Date(dt);
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}
export function daysBetweenInclusive(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
}
export function endOfMonthUTC(dt: Date, plusMonths = 0): Date {
  return new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + plusMonths + 1, 0));
}
export function todayUTC(now = new Date()): Date {
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}
/** How many dates in from..to (inclusive) fall on the given weekdays (0=Sun). */
export function countWeekdaysInRange(from: Date, to: Date, weekdays: number[]): number {
  const set = new Set(weekdays.map(Number));
  let count = 0;
  for (let d = new Date(from); d <= to; d = addDays(d, 1)) if (set.has(d.getUTCDay())) count++;
  return count;
}
/** Key used for per-range Order Qty overrides: "YYYY-MM-DD_YYYY-MM-DD". */
export function rangeKey(from: Date, to: Date): string {
  return `${formatDateISO(from)}_${formatDateISO(to)}`;
}

// ---------- Courses (fixed number of doses, every N hours) ----------
export interface CourseDose { n: number; date: string; time: string }
export interface CourseInfo {
  perDay: number; total: number; dose: number; everyMinutes: number;
  startDate: string; startTime: string;
  /** dose n (1-based) -> its date and "HH:MM" */
  at: (n: number) => CourseDose;
  last: CourseDose;
}
const hhmmOk = (s: unknown) => typeof s === 'string' && /^\d{2}:\d{2}$/.test(s);
/**
 * The course plan of a "course" medicine, or null when it isn't one (or is incomplete).
 * Times are wall-clock: 4 a day from 12:00 is 12:00, 18:00, 00:00, 06:00, …
 * Dose times never move: a skipped dose still counts as one of the total.
 */
export function courseInfo(med: Medicine): CourseInfo | null {
  if (med.schedule_type !== 'course') return null;
  const start = parseDateISO(med.start_date || '');
  const perDay = Math.min(6, Math.max(1, parseInt(String(med.course_per_day ?? 0), 10) || 0));
  const total = parseInt(String(med.course_total ?? 0), 10) || 0;
  if (!start || !perDay || total <= 0) return null;
  const startTime = hhmmOk(med.course_start_time) ? String(med.course_start_time) : '08:00';
  const [h, m] = startTime.split(':').map(Number);
  const s = h * 60 + m;
  const everyMinutes = Math.round(1440 / perDay);
  const at = (n: number): CourseDose => {
    const t = s + (n - 1) * everyMinutes;
    const day = Math.floor(t / 1440), min = t % 1440;
    return { n, date: formatDateISO(addDays(start, day)), time: `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}` };
  };
  const dose = parseFloat(String(med.course_dose ?? 1)) || 1;
  return { perDay, total, dose, everyMinutes, startDate: formatDateISO(start), startTime, at, last: at(total) };
}
/** The course doses that fall on one date. */
export function courseDosesOn(info: CourseInfo, dateISO: string): CourseDose[] {
  const start = parseDateISO(info.startDate)!, d = parseDateISO(dateISO);
  if (!d) return [];
  const day = Math.round((d.getTime() - start.getTime()) / 86400000);
  const s = info.at(1);
  const [h, m] = s.time.split(':').map(Number);
  const first = Math.max(1, Math.ceil((day * 1440 - (h * 60 + m)) / info.everyMinutes) + 1);
  const out: CourseDose[] = [];
  for (let n = first; n <= info.total; n++) {
    const x = info.at(n);
    if (x.date !== dateISO) break;
    out.push(x);
  }
  return out;
}

// ---------- Medicines ----------
/** End date = start + duration − 1; falls back to a legacy stored end_date. A course ends on its last dose's date. */
export function getEffectiveEndDate(med: Medicine): string {
  const course = courseInfo(med);
  if (course) return course.last.date;
  const start = parseDateISO(med.start_date || '');
  const duration = parseInt(med.duration_days as unknown as string, 10);
  if (start && duration > 0) return formatDateISO(addDays(start, duration - 1));
  return med.end_date || '';
}

export function formatMedicineName(med: Pick<Medicine, 'name' | 'strength' | 'strength_unit'>): string {
  const strength = String(med.strength || '').trim();
  const unit = med.strength_unit || 'mg';
  return strength ? `${med.name} ${strength} ${unit}` : med.name;
}

/** Prescription notation Morning+Lunch+Dinner, e.g. "1+0+1". */
export function formatCompactDoses(med: Medicine): string {
  const n = (v: unknown) => parseFloat((v || 0) as string);
  const morning = n(med.morning_before) + n(med.morning_after);
  const lunch = n(med.lunch_before) + n(med.lunch_after);
  const dinner = n(med.dinner_before) + n(med.dinner_after);
  return `${fmtNum(morning)}+${fmtNum(lunch)}+${fmtNum(dinner)}`;
}

export function unknownType(id?: string): MedicineType {
  return { id: id || 'unknown', name: id || 'Unknown', icon: '❓', unit: '', round_enabled: true, round_to: 5 };
}
export function getTypeInfo(types: MedicineType[], id: string): MedicineType {
  return types.find(t => t.id === id) || unknownType(id);
}

/**
 * How many units are needed between `from` and `to` (both inclusive).
 * `person` is only used for "skip on dialysis day".
 */
export function computeRangeNeed(med: Medicine, from: Date, to: Date, person?: Person | null): NeedResult {
  const startD = parseDateISO(med.start_date || '');
  const endD = parseDateISO(getEffectiveEndDate(med));

  if (endD && endD < from) return { schedule: `Ended ${formatDateDMY(endD)}`, needed: 0, status: 'ended' };
  if (startD && startD > to) return { schedule: `Starts ${formatDateDMY(startD)}`, needed: 0, status: 'not_started' };

  let status: NeedStatus = 'normal';
  let suffix = '';
  let rs = from, re = to;
  if (startD && startD > from) { rs = startD; status = 'partial'; suffix += `  (starts ${formatDateDMY(startD)})`; }
  if (endD && endD < to) { re = endD; status = 'partial'; suffix += `  (stops ${formatDateDMY(endD)})`; }

  const effectiveDays = Math.max(0, daysBetweenInclusive(rs, re));
  const num = (v: unknown) => parseFloat((v || 0) as string);

  if (med.schedule_type === 'dialysis') {
    const perSession = num(med.dialysis_per_session);
    const perWeek = num(med.dialysis_per_week);
    const sessions = perWeek > 0 ? Math.ceil(effectiveDays * perWeek / 7) : 0;
    const needed = Math.ceil(perSession * sessions);
    let sched = `Dialysis (${fmtNum(perWeek)}×/wk)`;
    if (perSession !== 1) sched = `Dialysis (${fmtNum(perSession)} ×${fmtNum(perWeek)}/wk)`;
    return { schedule: sched + suffix, needed, status, dateSuffix: suffix };
  }

  if (med.schedule_type === 'interval') {
    const dose = num(med.interval_dose);
    const intervalDays = Math.max(1, parseInt(med.interval_days as unknown as string, 10) || 1);
    let occurrences = 0;
    if (startD) {
      for (let d = new Date(rs); d <= re; d = addDays(d, 1)) {
        const diffDays = Math.round((d.getTime() - startD.getTime()) / 86400000);
        if (diffDays >= 0 && diffDays % intervalDays === 0) occurrences++;
      }
    } else {
      occurrences = Math.ceil(effectiveDays / intervalDays);
    }
    const needed = Math.ceil(dose * occurrences);
    const sched = `Every ${intervalDays} day${intervalDays === 1 ? '' : 's'} × ${fmtNum(dose)}`;
    return { schedule: sched + suffix, needed, status, dateSuffix: suffix };
  }

  if (med.schedule_type === 'course') {
    const c = courseInfo(med);
    let count = 0;
    if (c) for (let d = new Date(rs); d <= re; d = addDays(d, 1)) count += courseDosesOn(c, formatDateISO(d)).length;
    const needed = c ? Math.ceil(c.dose * count) : 0;
    const sched = c ? `Course: ${c.perDay}×/day (every ${fmtNum(c.everyMinutes / 60)} h) × ${fmtNum(c.dose)}, ${c.total} doses` : 'Course (incomplete)';
    return { schedule: sched + suffix, needed, status, dateSuffix: suffix };
  }

  if (med.schedule_type === 'fixed') {
    const qty = num(med.fixed_qty_per_month);
    const every = Math.max(1, parseInt(med.fixed_every_days as unknown as string, 10) || 30);
    const needed = Math.ceil(qty * effectiveDays / every);
    const sched = `${fmtNum(qty)} lasts ${every} day${every === 1 ? '' : 's'}`;
    return { schedule: sched + suffix, needed, status, dateSuffix: suffix };
  }

  const mb = num(med.morning_before), ma = num(med.morning_after);
  const lb = num(med.lunch_before), la = num(med.lunch_after);
  const db = num(med.dinner_before), da = num(med.dinner_after);
  const perDay = mb + ma + lb + la + db + da;
  let neededRaw = perDay * effectiveDays;

  let dialysisSkipSuffix = '';
  if (med.skip_on_dialysis_day) {
    const dialysisDays = person && Array.isArray(person.dialysis_days) ? person.dialysis_days : [];
    if (dialysisDays.length) {
      const dialysisTime = (person && person.dialysis_time) || 'morning';
      // Morning session skips the morning dose; noon/evening sessions skip lunch.
      const affectedSlotDose = dialysisTime === 'morning' ? mb + ma : lb + la;
      if (affectedSlotDose > 0) {
        const dialysisDayCount = countWeekdaysInRange(rs, re, dialysisDays);
        if (dialysisDayCount > 0) {
          neededRaw -= affectedSlotDose * dialysisDayCount;
          dialysisSkipSuffix = `  (−${fmtNum(affectedSlotDose * dialysisDayCount)} on ${dialysisDayCount} dialysis day${dialysisDayCount === 1 ? '' : 's'})`;
        }
      }
    }
  }

  const needed = Math.ceil(Math.max(0, neededRaw));
  const slotMap: [number, string][] = [
    [mb, 'Morning - BBF'], [ma, 'Morning - ABF'],
    [lb, 'Lunch - BL'], [la, 'Lunch - AL'],
    [db, 'Dinner - BD'], [da, 'Dinner - AD'],
  ];
  const parts = slotMap.filter(([c]) => c > 0).map(([c, label]) => `${label} × ${fmtNum(c)}`);
  const sched = parts.length ? parts.join(' + ') : '—';
  return { schedule: sched + suffix + dialysisSkipSuffix, needed, status, dateSuffix: suffix };
}

export interface OrderLine extends NeedResult { onHand: number; toBuy: number; qty: number; isOverride: boolean }

/**
 * The full purchase line for one medicine: Need, Have, To Buy and Order Qty.
 * Order Qty honours a manual override typed for this exact date range,
 * otherwise rounds up to the type's pack size (exact for partial ranges or
 * types with rounding turned off).
 */
export function computeOrderLine(med: Medicine, from: Date, to: Date, person: Person | null | undefined, types: MedicineType[]): OrderLine {
  const need = computeRangeNeed(med, from, to, person);
  const onHand = parseInt((med.on_hand || 0) as unknown as string, 10);
  const toBuy = Math.max(0, need.needed - onHand);
  if (need.status === 'ended' || need.status === 'not_started') return { ...need, onHand, toBuy, qty: 0, isOverride: false };
  const overrides = med.qty_overrides || {};
  const key = rangeKey(from, to);
  if (key in overrides) {
    const v = parseInt(overrides[key] as unknown as string, 10);
    if (!Number.isNaN(v)) return { ...need, onHand, toBuy, qty: v, isOverride: true };
  }
  const typeInfo = getTypeInfo(types, med.type);
  const roundingOn = typeInfo.round_enabled !== false;
  const qty = need.status === 'partial' || !roundingOn ? exactQuantity(toBuy) : roundPharmacy(toBuy, typeInfo.round_to);
  return { ...need, onHand, toBuy, qty, isOverride: false };
}

/** Totals per type name for the summary line, e.g. { Tablet: 90, Injection: 4 }. */
export function orderTotalsByType(lines: { med: Medicine; line: OrderLine }[], types: MedicineType[]): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const { med, line } of lines) {
    if (line.qty > 0) {
      const name = getTypeInfo(types, med.type).name;
      totals[name] = (totals[name] || 0) + line.qty;
    }
  }
  return totals;
}

export function genId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// Doses due on a day, stock left, blood pressure (mobile app + Daily log).
export * from './doses';
export * from './vaccines';
export * from './visits';
